import { Injectable } from '@angular/core';
import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Observable, catchError, throwError } from 'rxjs';
import { environment } from '../../environments/environment';

interface ApiHttpFallbackConfig {
  /** e.g. https://api.financeqa.centaiva.com */
  httpsOrigin: string;
  /** e.g. http://api.financeqa.centaiva.com */
  httpOrigin: string;
  /** How long to stay on http before trying https again. */
  retryHttpsAfterMinutes: number;
}

const STORAGE_KEY = 'ww.apiHttpFallbackUntil';

/**
 * QA stopgap: calls the API over https first and, when https fails before any response
 * (status 0: certificate/TLS error or connection reset), retries the same request over http
 * and stays on http for a while before probing https again. Only active when the environment
 * configures apiHttpFallback (QA), and only when the page itself is http - an https page
 * cannot call http at all (mixed content).
 */
@Injectable()
export class ApiHttpsFallbackInterceptor implements HttpInterceptor {
  private readonly config = (environment as { apiHttpFallback?: ApiHttpFallbackConfig }).apiHttpFallback;
  private httpUntil = readStoredUntil();

  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    const config = this.config;
    if (!config || !req.url.startsWith(`${config.httpsOrigin}/`) || !pageIsHttp()) {
      return next.handle(req);
    }

    if (Date.now() < this.httpUntil) {
      return next.handle(this.toHttp(req, config));
    }

    return next.handle(req).pipe(
      catchError((error: unknown) => {
        if (!(error instanceof HttpErrorResponse) || error.status !== 0) {
          return throwError(() => error);
        }
        this.httpUntil = Date.now() + config.retryHttpsAfterMinutes * 60_000;
        storeUntil(this.httpUntil);
        console.warn(`HTTPS to ${config.httpsOrigin} failed; using ${config.httpOrigin} for ${config.retryHttpsAfterMinutes} min.`);
        return next.handle(this.toHttp(req, config));
      }),
    );
  }

  private toHttp(req: HttpRequest<unknown>, config: ApiHttpFallbackConfig): HttpRequest<unknown> {
    return req.clone({ url: config.httpOrigin + req.url.slice(config.httpsOrigin.length) });
  }
}

function pageIsHttp(): boolean {
  return typeof location !== 'undefined' && location.protocol === 'http:';
}

function readStoredUntil(): number {
  try {
    return Number(sessionStorage.getItem(STORAGE_KEY)) || 0;
  } catch {
    return 0;
  }
}

function storeUntil(until: number): void {
  try {
    sessionStorage.setItem(STORAGE_KEY, String(until));
  } catch {
    // storage unavailable: the in-memory value still applies for this page load
  }
}
